import { Component, inject, OnInit, effect } from '@angular/core';
import { Router } from '@angular/router';
import { LayoutComponent } from './layout/components/layout/layout.component';
import { FormsModule } from '@angular/forms';
import { OidcSecurityService } from 'angular-auth-oidc-client';

import { DashboardStore } from './dashboard/stores/dashboard.store';
import { UserStore } from './shared/user/store/user.store';
import { NectaModuleStore } from './necta-module/store/necta-module.store';
import { AppLoadingOverlayComponent } from "./shared/components/loading-overlay/loading-overlay.component";

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [FormsModule, LayoutComponent, AppLoadingOverlayComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'] // Angular erwartet hier "styleUrls" (Plural)
})
export class AppComponent implements OnInit {
  // Titel der Anwendung, der im Template genutzt werden kann.
  title = 'necta-client';
  private readonly userStore = inject(UserStore);
  private readonly dashboardStore = inject(DashboardStore);
  private readonly moduleStore = inject(NectaModuleStore);
  private isRegisteredNectaOne = this.userStore.isRegisteredNectaOne;
  private readonly router = inject(Router);


  // Injektion des OIDC-Security-Services, der für Authentifizierung und Autorisierung zuständig ist.
  private readonly oidcSecurityService = inject(OidcSecurityService);

constructor() {
  // Start des Ladens
effect(() => {
  const registered = this.isRegisteredNectaOne();
  const tenant = this.userStore.activeTenant();

  // null = checkRegistrations noch nicht zurueck -> nichts tun, sonst landet jeder
  // User initial faelschlich auf /dashboard/welcome.
  if (registered === null) return;

  if (registered === true && tenant) {
    this.dashboardStore.loadUserDashboards();
  } else if (registered === false) {
    this.router.navigate(['/dashboard', 'welcome']);
  }
  // Fall "registered && !tenant": nichts tun — wir warten auf activeTenant.
});

  // Tenant-spezifische Module laden, sobald ein Tenant gesetzt ist (initial + bei Wechsel).
  // Erspart den manuellen loadNectaModules-Aufruf in der active-tenant-menu Komponente.
  effect(() => {
    const tenant = this.userStore.activeTenant();
    if (tenant) {
      this.moduleStore.loadNectaModules();
    }
  });

  // Reaktion auf erfolgreiches Laden
  effect(() => {
    const registered = this.isRegisteredNectaOne();
    const dashboards = this.dashboardStore.dashboards();

    // Nur registrierte User navigieren auf Basis ihrer Dashboard-Liste. Sonst wuerde
    // das initial im Store gehaltene Welcome-Dashboard (id=-1) sofort zu /welcome routen,
    // bevor wir wissen, ob der User registriert ist.
    if (registered !== true) return;

    if (dashboards.length > 0 ) {
      const firstId = dashboards[0].id === 0 ? -1 : dashboards[0].id; // Wenn die ID 0 ist, dann -1 verwenden
      if(firstId !== -1) {
        this.router.navigate(['/dashboard', firstId, 'view']);
      } else {
        this.router.navigate(['/dashboard', 'welcome']);
      }
    }
  });
}

  /**
   * Lifecycle-Hook, der beim Initialisieren der Komponente ausgeführt wird.
   * Hier wird die Authentifizierung überprüft und der aktuelle Token ausgegeben.
   */
  ngOnInit(): void {
    this.oidcSecurityService.checkAuth().subscribe({
      next: ({ isAuthenticated }) => {
        // Erfolgreicher Callback -> evtl. gesetztes Recover-Flag zuruecksetzen.
        sessionStorage.removeItem('oidc_recover');

        if (isAuthenticated) {
          this.userStore.checkRegistrations().subscribe();
        }
      },
      error: (err) => {
        const msg = String(err?.message ?? err);

        // Eingefrorener/fremder Authorize-State (z.B. nach Passwort-Reset ueber den
        // Mail-Umweg oder gebookmarkte Login-Seite): checkAuth() findet keine zum
        // zurueckkommenden state passende Config. Wir starten einmalig einen frischen
        // Authorize-Flow (frischer state/nonce/code_verifier). Ist der User am
        // IdentityServer bereits per Cookie angemeldet, laeuft das still per SSO durch.
        if (msg.includes('could not find matching config for state')) {
          if (!sessionStorage.getItem('oidc_recover')) {
            sessionStorage.setItem('oidc_recover', '1');
            this.oidcSecurityService.authorize(); // frischen Flow starten
          } else {
            // Recovery bereits versucht -> kein erneuter Retry (Loop-Schutz),
            // sondern definierte Fehlerseite.
            this.router.navigate(['/unauthorized']);
          }
        }
      }
    });
  }



  /**
   * Startet den Login-Prozess.
   * Wird aufgerufen, wenn der Nutzer sich anmelden möchte.
   */
  login(): void {
    this.oidcSecurityService.authorize();
  }

  /**
   * Erneuert die aktuelle Sitzung.
   * Hier wird zur Vereinfachung der Ablaufsteuerung dieselbe Methode wie beim Login verwendet.
   */
  refreshSession(): void {
    this.oidcSecurityService.authorize();
  }


}
